import { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Skeleton,
  Divider,
  Chip,
  Button,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack,
  Assignment,
  AssignmentReturn,
  LocationOn,
  Person,
  Architecture,
  CalendarToday,
  Add,
  Build,
  Delete,
  Edit,
  PictureAsPdf,
  Directions,
  Share,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import theme from "../../theme/theme";
import { fetchProjectById, deleteProject } from "../../api/ProjectService";
import {
  fetchServices,
  createService,
  updateService,
  deleteService,
  fetchServiceById,
  updateServiceCalendarEntry,
  openAmcPdf,
} from "../../api/ServiceService";
import { fetchEmployeesByDesignation } from "../../api/EmployeeService";
import {
  fetchProjectItems,
  assignItemToProject,
  returnItemFromProject,
  fetchAllWarehouses,
  fetchWarehouseItems,
} from "../../api/InventoryService";
import TableComponent from "../../components/Table";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from "@mui/material";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import CreateServiceDialog from "../../components/service/CreateServiceDialog";
import ServiceCalendar from "../../components/service/ServiceCalendar";
import ServiceEntryDialog from "../../components/service/ServiceEntryDialog";
import ConfirmDialog from "../../components/ConfirmDialog";
import ServiceHistorySection from "../../components/service/ServiceHistorySection";

// Leaflet icon setup
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const InfoRow = ({ icon, label, value }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}>
    <Box
      sx={{
        color: theme.palette.primary.main,
        display: "flex",
        alignItems: "center",
      }}
    >
      {icon}
    </Box>
    <Box sx={{ flex: 1 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", lineHeight: 1.2 }}
      >
        {label}
      </Typography>
      <Typography variant="body2" fontWeight="500">
        {value || "-"}
      </Typography>
    </Box>
  </Box>
);

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatEnum = (val) => {
  if (!val) return "-";
  return val
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const ProjectDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  // Inventory State
  const [projectItems, setProjectItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseItems, setWarehouseItems] = useState([]);
  const [loadingWarehouseItems, setLoadingWarehouseItems] = useState(false);
  const [openItemDialog, setOpenItemDialog] = useState(false);
  const [savingItem, setSavingItem] = useState(false);
  const [itemFormData, setItemFormData] = useState({
    warehouse_id: "",
    item_id: "",
    quantity: "",
    notes: "",
    performed_by: "",
  });

  // Return State
  const [openReturnDialog, setOpenReturnDialog] = useState(false);
  const [returnTarget, setReturnTarget] = useState(null);
  const [savingReturn, setSavingReturn] = useState(false);
  const [returnFormData, setReturnFormData] = useState({
    quantity: "",
    warehouse_id: "",
    notes: "",
    performed_by: "",
  });

  // Service state
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [calendarEntries, setCalendarEntries] = useState([]);
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [savingService, setSavingService] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [amcLoading, setAmcLoading] = useState(null);

  // Entry dialog state
  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [savingEntry, setSavingEntry] = useState(false);
  const [employees, setEmployees] = useState([]);

  // Delete confirm state
  const [confirmProjectDelete, setConfirmProjectDelete] = useState(false);
  const [deletingProject, setDeletingProject] = useState(false);
  const [confirmServiceDelete, setConfirmServiceDelete] = useState(false);
  const [serviceDeleteTarget, setServiceDeleteTarget] = useState(null);
  const [deletingService, setDeletingService] = useState(false);

  const fetchProject = async () => {
    setLoading(true);
    try {
      const response = await fetchProjectById(id);
      setProject(response.data.data ? response.data.data : response.data);
    } catch (error) {
      toast.error(error.message || "Failed to fetch project details");
    } finally {
      setLoading(false);
    }
  };

  const loadServices = async () => {
    setLoadingServices(true);
    try {
      const response = await fetchServices({ project_id: id });
      const data = response.data?.data || response.data || [];
      const list = Array.isArray(data) ? data : [];
      setServices(list);
      return list;
    } catch (error) {
      toast.error("Failed to load services");
      return [];
    } finally {
      setLoadingServices(false);
    }
  };

  const loadServiceCalendar = async (serviceId) => {
    setLoadingCalendar(true);
    try {
      const response = await fetchServiceById(serviceId);
      const data = response.data?.data || response.data;
      setCalendarEntries(data?.calendar_entries || []);
    } catch (error) {
      toast.error("Failed to load service calendar");
    } finally {
      setLoadingCalendar(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const response = await fetchEmployeesByDesignation("service");
      const data = response.data?.data || response.data || [];
      setEmployees(Array.isArray(data) ? data : []);
    } catch {
      // silently fail, employees are optional
    }
  };

  const handleSaveService = async (formData) => {
    setSavingService(true);
    try {
      if (editingService) {
        await updateService(editingService.id, formData);
        toast.success("Service plan updated successfully");
      } else {
        await createService({ ...formData, project_id: parseInt(id) });
        toast.success("Service plan created successfully");
      }
      setCreateDialogOpen(false);
      const list = await loadServices();
      // Keep the selected service (and its calendar) fresh after an edit.
      if (editingService && selectedService?.id === editingService.id) {
        const updated = list.find((s) => s.id === editingService.id);
        if (updated) setSelectedService(updated);
        loadServiceCalendar(editingService.id);
      }
      setEditingService(null);
    } catch (error) {
      const msg = error.response?.data?.message || "Failed to save service";
      toast.error(msg);
    } finally {
      setSavingService(false);
    }
  };

  const handleEditServiceClick = (service) => {
    setEditingService(service);
    setCreateDialogOpen(true);
  };

  const handleAmcPdf = async (service) => {
    setAmcLoading(service.id);
    try {
      await openAmcPdf(service.id);
    } catch (error) {
      toast.error(error.message || "Failed to open AMC PDF");
    } finally {
      setAmcLoading(null);
    }
  };

  const handleEntryClick = (entry) => {
    setSelectedEntry(entry);
    setEntryDialogOpen(true);
  };

  const handleEntrySave = async (entryId, payload) => {
    setSavingEntry(true);
    try {
      await updateServiceCalendarEntry(entryId, payload);
      toast.success("Service entry updated");
      setEntryDialogOpen(false);
      setSelectedEntry(null);
      // Reload calendar
      if (selectedService) {
        await loadServiceCalendar(selectedService.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update entry");
    } finally {
      setSavingEntry(false);
    }
  };

  const loadProjectInventory = async () => {
    try {
      const [pItems, wList] = await Promise.all([
        fetchProjectItems(id).catch(() => ({ data: [] })),
        fetchAllWarehouses().catch(() => ({ data: [] })),
      ]);
      setProjectItems(pItems.data || []);
      setWarehouses(wList.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const loadWarehouseItemsForDialog = async (warehouseId) => {
    setLoadingWarehouseItems(true);
    setWarehouseItems([]);
    try {
      const res = await fetchWarehouseItems(warehouseId);
      setWarehouseItems(res.data || []);
    } catch {
      setWarehouseItems([]);
    } finally {
      setLoadingWarehouseItems(false);
    }
  };

  useEffect(() => {
    if (project) {
      loadProjectInventory();
    }
  }, [project]);

  const handleAssignItem = async () => {
    if (
      !itemFormData.warehouse_id ||
      !itemFormData.item_id ||
      !itemFormData.quantity ||
      !itemFormData.performed_by
    ) {
      return toast.warning(
        "Warehouse, Item, Quantity, and Employee are required",
      );
    }
    const selectedWi = warehouseItems.find((wi) => wi.item_id === itemFormData.item_id);
    if (!selectedWi || selectedWi.quantity <= 0) {
      return toast.error("Stock is zero. Cannot assign this item.");
    }
    if (itemFormData.quantity > selectedWi.quantity) {
      return toast.error(`Insufficient stock. Available: ${selectedWi.quantity}`);
    }
    setSavingItem(true);
    try {
      await assignItemToProject(id, itemFormData);
      toast.success("Item assigned to project");
      setOpenItemDialog(false);
      setItemFormData({
        warehouse_id: "",
        item_id: "",
        quantity: "",
        notes: "",
        performed_by: "",
      });
      loadProjectInventory();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to assign item");
    } finally {
      setSavingItem(false);
    }
  };

  const remainingQty = (row) =>
    Number(row?.quantity || 0) - Number(row?.returned_quantity || 0);

  const handleOpenReturn = (row) => {
    if (remainingQty(row) <= 0) {
      return toast.info("This material has already been fully returned.");
    }
    setReturnTarget(row);
    setReturnFormData({
      quantity: "",
      warehouse_id: row.inventory_transaction?.warehouse_id || "",
      notes: "",
      performed_by: "",
    });
    setOpenReturnDialog(true);
  };

  const handleReturnItem = async () => {
    if (!returnTarget) return;
    if (!returnFormData.quantity || !returnFormData.performed_by) {
      return toast.warning("Quantity and Employee are required");
    }
    const remaining = remainingQty(returnTarget);
    if (returnFormData.quantity > remaining) {
      return toast.error(`Cannot return more than assigned. Remaining: ${remaining}`);
    }
    setSavingReturn(true);
    try {
      await returnItemFromProject(id, returnTarget.id, returnFormData);
      toast.success("Material returned to stock");
      setOpenReturnDialog(false);
      setReturnTarget(null);
      loadProjectInventory();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to return material");
    } finally {
      setSavingReturn(false);
    }
  };

  useEffect(() => {
    fetchProject();
    loadServices();
    loadEmployees();
  }, [id]);

  // Auto-select the first service
  useEffect(() => {
    if (services.length > 0 && !selectedService) {
      setSelectedService(services[0]);
      loadServiceCalendar(services[0].id);
    }
  }, [services]);

  const handleServiceSelect = (service) => {
    setSelectedService(service);
    loadServiceCalendar(service.id);
  };

  const handleDeleteProject = async () => {
    setDeletingProject(true);
    try {
      await deleteProject(id);
      toast.success("Project deleted successfully");
      navigate("/projects");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete project");
    } finally {
      setDeletingProject(false);
      setConfirmProjectDelete(false);
    }
  };

  const handleDeleteServiceClick = (service) => {
    setServiceDeleteTarget(service);
    setConfirmServiceDelete(true);
  };

  const handleConfirmDeleteService = async () => {
    if (!serviceDeleteTarget) return;
    setDeletingService(true);
    try {
      await deleteService(serviceDeleteTarget.id);
      toast.success("Service deleted successfully");
      if (selectedService?.id === serviceDeleteTarget.id) {
        setSelectedService(null);
        setCalendarEntries([]);
      }
      setConfirmServiceDelete(false);
      setServiceDeleteTarget(null);
      await loadServices();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete service");
    } finally {
      setDeletingService(false);
    }
  };

  const hasCoordinates = project?.latitude && project?.longitude;

  const handleDirections = () => {
    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${project.latitude},${project.longitude}`,
      "_blank",
    );
  };

  const handleShareLocation = async () => {
    const url = `https://www.google.com/maps/search/?api=1&query=${project.latitude},${project.longitude}`;
    const title = project.project_name || project.project_id || "Project Location";
    if (navigator.share) {
      try {
        await navigator.share({
          title,
          text: `Location of ${title}`,
          url,
        });
        return;
      } catch (err) {
        if (err?.name === "AbortError") return; // user cancelled the share sheet
        // fall through to clipboard copy
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Location link copied to clipboard");
    } catch {
      toast.error("Failed to copy location link");
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton onClick={() => navigate("/projects")}>
            <ArrowBack sx={{ color: theme.palette.primary.main }} />
          </IconButton>
          <Box>
            <Typography variant="h5" fontWeight="700">
              {loading ? (
                <Skeleton width={200} />
              ) : (
                `Project #${project?.project_id}${project?.project_name ? ` - ${project.project_name}` : ''}`
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {loading ? (
                <Skeleton width={120} />
              ) : (
                `Created on: ${formatDate(project?.created_at)}`
              )}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {!loading && project && (
            <Chip
              label={formatEnum(project.status)}
              color={
                project.status === "handed_over"
                  ? "success"
                  : project.status === "under_construction"
                    ? "warning"
                    : "info"
              }
              variant="outlined"
              sx={{ fontWeight: 600, px: 1 }}
            />
          )}
          {!loading && project && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<Delete />}
              onClick={() => setConfirmProjectDelete(true)}
              sx={{
                borderRadius: theme.button.borderRadius,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Delete Project
            </Button>
          )}
        </Box>
      </Box>

      {/* Top Row: Specs + Client & Origin */}
      <Box sx={{ display: "flex", gap: 2.5 }}>
        {/* Left Column — 50% (Project Specs) */}
        <Box sx={{ flex: "0 0 50%", minWidth: 0 }}>
          <Paper sx={{ p: 3, borderRadius: theme.paper.borderRadius, mb: 2 }}>
            <Typography
              variant="subtitle1"
              fontWeight="700"
              color="primary"
              sx={{ mb: 2 }}
            >
              Configuration Specs
            </Typography>
            {loading ? (
              <Skeleton variant="rectangular" height={200} />
            ) : (
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 1 }}>
                <InfoRow
                  icon={<Architecture fontSize="small" />}
                  label="Lift Type"
                  value={formatEnum(project?.lift_type)}
                />
                <Divider />
                <InfoRow
                  icon={<Assignment fontSize="small" />}
                  label="Cabin Type"
                  value={project?.cabin_type}
                />
                <Divider />
                <InfoRow
                  icon={<Assignment fontSize="small" />}
                  label="Configuration"
                  value={formatEnum(project?.lift_configuration)}
                />
                <Divider />
                <InfoRow
                  icon={<Assignment fontSize="small" />}
                  label="Door Type"
                  value={
                    project?.door_type === "manual" && project?.is_swing_door
                      ? `${formatEnum(project?.door_type)} (Swing)`
                      : formatEnum(project?.door_type)
                  }
                />
                <Divider />
                {project?.door_type === "automatic" && (
                  <>
                    <InfoRow
                      icon={<Assignment fontSize="small" />}
                      label="Vision Type"
                      value={formatEnum(project?.vision_type)}
                    />
                    <Divider />
                  </>
                )}
                <InfoRow
                  icon={<Assignment fontSize="small" />}
                  label="Capacity (Persons)"
                  value={project?.capacity_persons}
                />
                <Divider />
                <InfoRow
                  icon={<Assignment fontSize="small" />}
                  label="Capacity (Kg)"
                  value={project?.capacity_kg}
                />
                <Divider />
                <InfoRow
                  icon={<Assignment fontSize="small" />}
                  label="Total Floors"
                  value={project?.no_of_floors}
                />
                <Divider />
                <InfoRow
                  icon={<Assignment fontSize="small" />}
                  label="Basements"
                  value={project?.no_of_basements}
                />
                <Divider />
                <InfoRow
                  icon={<Assignment fontSize="small" />}
                  label="Shaft Dimensions (L x W)"
                  value={`${project?.shaft_length || "?"} x ${project?.shaft_width || "?"}`}
                />
              </Box>
            )}
          </Paper>
        </Box>

        {/* Right Column — 50% (Client & Location + Origin) */}
        <Box sx={{ flex: "0 0 50%", minWidth: 0 }}>
          <Paper sx={{ p: 3, borderRadius: theme.paper.borderRadius, mb: 2 }}>
            <Typography
              variant="subtitle1"
              fontWeight="700"
              color="primary"
              sx={{ mb: 2 }}
            >
              Client & Location
            </Typography>
            {loading ? (
              <Skeleton variant="rectangular" height={150} />
            ) : (
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 1 }}>
                <InfoRow
                  icon={<Person fontSize="small" />}
                  label="Customer Name"
                  value={project?.customer?.name}
                />
                <Divider />
                <InfoRow
                  icon={<LocationOn fontSize="small" />}
                  label="Zone / Region"
                  value={project?.zone?.name}
                />
                <Divider />
                <InfoRow
                  icon={<LocationOn fontSize="small" />}
                  label="City"
                  value={project?.city?.name}
                />
              </Box>
            )}
          </Paper>

          <Paper sx={{ p: 3, borderRadius: theme.paper.borderRadius }}>
            <Typography
              variant="subtitle1"
              fontWeight="700"
              color="primary"
              sx={{ mb: 2 }}
            >
              Origin Trace
            </Typography>
            {loading ? (
              <Skeleton variant="rectangular" height={80} />
            ) : project?.lead_id ? (
              <Box
                onClick={() => navigate(`/leads/${project.lead_id}`)}
                sx={{
                  p: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  cursor: "pointer",
                  transition: "all 0.2s",
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: theme.palette.primary.main + "06",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Assignment fontSize="small" color="primary" />
                  <Typography variant="body1" fontWeight="600">
                    Generated from Lead #{project.lead_id}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Project was deployed manually (No Lead attached).
              </Typography>
            )}
          </Paper>
        </Box>
      </Box>

      {/* Bottom Row: Service Management (left) + Map (right) */}
      {!loading && (
        <Box sx={{ display: "flex", gap: 2.5, mt: 2.5 }}>
          {/* Service Management — left */}
          <Box
            sx={{
              flex: hasCoordinates ? "0 0 60%" : "1 1 100%",
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Paper
              sx={{
                p: 3,
                borderRadius: theme.paper.borderRadius,
                flexGrow: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Build
                    fontSize="small"
                    sx={{ color: theme.palette.primary.main }}
                  />
                  <Typography
                    variant="subtitle1"
                    fontWeight="700"
                    color="primary"
                  >
                    Service Management
                  </Typography>
                  {services.length > 0 && (
                    <Chip
                      label={services.length}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        backgroundColor: theme.palette.primary.main + "18",
                        color: theme.palette.primary.main,
                        ml: 0.5,
                      }}
                    />
                  )}
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Add />}
                  onClick={() => setCreateDialogOpen(true)}
                  sx={{
                    borderRadius: theme.button.borderRadius,
                    textTransform: "none",
                    backgroundColor: theme.palette.primary.main,
                  }}
                >
                  New Service Plan
                </Button>
              </Box>

              {loadingServices ? (
                <Box>
                  <Skeleton variant="rounded" height={50} sx={{ mb: 1 }} />
                  <Skeleton variant="rounded" height={300} />
                </Box>
              ) : services.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 6,
                    border: "1px dashed",
                    borderColor: "divider",
                    borderRadius: 2,
                  }}
                >
                  <Build sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    No service plans for this project yet
                  </Typography>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setCreateDialogOpen(true)}
                    sx={{
                      mt: 1,
                      textTransform: "none",
                      color: theme.palette.primary.main,
                    }}
                  >
                    Create first service plan
                  </Button>
                </Box>
              ) : (
                <Box>
                  {/* Service Plan Cards */}
                  <Box sx={{ mb: 2 }}>
                    {services.map((service) => (
                      <Box
                        key={service.id}
                        onClick={() => handleServiceSelect(service)}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 1,
                          p: 1.5,
                          mb: 1,
                          borderRadius: 2,
                          cursor: "pointer",
                          border: "1px solid",
                          borderColor:
                            selectedService?.id === service.id
                              ? theme.palette.primary.main
                              : "divider",
                          backgroundColor:
                            selectedService?.id === service.id
                              ? theme.palette.primary.main + "0D"
                              : "transparent",
                          "&:hover": {
                            borderColor: theme.palette.primary.main,
                          },
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              flexWrap: "wrap",
                            }}
                          >
                            <Typography variant="body2" fontWeight="700">
                              {formatEnum(service.service_type)}
                            </Typography>
                            {service.reference_no && (
                              <Chip
                                label={service.reference_no}
                                size="small"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: 11,
                                  height: 20,
                                  backgroundColor:
                                    theme.palette.primary.main + "18",
                                  color: theme.palette.primary.main,
                                }}
                              />
                            )}
                          </Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            {formatDate(service.start_date)} —{" "}
                            {formatDate(service.end_date)} · Every{" "}
                            {service.interval_in_days} days ·{" "}
                            {service.number_of_services} visits
                            {service.service_type === "paid_service"
                              ? ` · ₹${service.price}`
                              : ""}
                          </Typography>
                        </Box>
                        <Box
                          sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Tooltip title="AMC PDF">
                            <span>
                              <IconButton
                                size="small"
                                disabled={amcLoading === service.id}
                                onClick={() => handleAmcPdf(service)}
                                sx={{ color: theme.palette.primary.main }}
                              >
                                <PictureAsPdf fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Edit Service Plan">
                            <IconButton
                              size="small"
                              onClick={() => handleEditServiceClick(service)}
                              sx={{ color: "#f0a500" }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Service Plan">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteServiceClick(service)}
                              sx={{ color: "#ff5252" }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    ))}
                  </Box>

                  {/* Service Details Summary */}
                  {selectedService && (
                    <Box
                      sx={{
                        display: "flex",
                        gap: 3,
                        mb: 2,
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: "#f8f9fa",
                        border: "1px solid",
                        borderColor: "divider",
                        flexWrap: "wrap",
                      }}
                    >
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          Type
                        </Typography>
                        <Typography variant="body2" fontWeight="600">
                          {formatEnum(selectedService.service_type)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          Interval
                        </Typography>
                        <Typography variant="body2" fontWeight="600">
                          Every {selectedService.interval_in_days} days
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          Total Services
                        </Typography>
                        <Typography variant="body2" fontWeight="600">
                          {selectedService.number_of_services}
                        </Typography>
                      </Box>
                      {selectedService.service_type === "paid_service" && (
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            Price / Visit
                          </Typography>
                          <Typography variant="body2" fontWeight="600">
                            ₹{selectedService.price}
                          </Typography>
                        </Box>
                      )}
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          Period
                        </Typography>
                        <Typography variant="body2" fontWeight="600">
                          {formatDate(selectedService.start_date)} —{" "}
                          {formatDate(selectedService.end_date)}
                        </Typography>
                      </Box>
                      {selectedService.reference_no && (
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            AMC Ref No
                          </Typography>
                          <Typography variant="body2" fontWeight="600">
                            {selectedService.reference_no}
                          </Typography>
                        </Box>
                      )}
                      {selectedService.lift_description && (
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            Lift Description
                          </Typography>
                          <Typography variant="body2" fontWeight="600">
                            {selectedService.lift_description}
                          </Typography>
                        </Box>
                      )}
                      {selectedService.bank && (
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            Bank
                          </Typography>
                          <Typography variant="body2" fontWeight="600">
                            {selectedService.bank.name}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Calendar */}
                  <ServiceCalendar
                    entries={calendarEntries}
                    loading={loadingCalendar}
                    onEntryClick={handleEntryClick}
                  />
                </Box>
              )}
            </Paper>
          </Box>

          {/* Map — right (only if coordinates exist) */}
          {hasCoordinates && (
            <Box
              sx={{
                flex: "0 0 40%",
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Paper
                sx={{
                  p: 3,
                  borderRadius: theme.paper.borderRadius,
                  flexGrow: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="700"
                    color="primary"
                  >
                    Project Location
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<Directions />}
                      onClick={handleDirections}
                      sx={{
                        borderRadius: theme.button.borderRadius,
                        textTransform: "none",
                        fontWeight: 600,
                        backgroundColor: theme.palette.primary.main,
                      }}
                    >
                      Directions
                    </Button>
                    <Tooltip title="Share location link">
                      <IconButton
                        size="small"
                        onClick={handleShareLocation}
                        sx={{
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 2,
                          color: theme.palette.primary.main,
                        }}
                      >
                        <Share fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                <Box
                  sx={{
                    height: 400,
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <MapContainer
                    center={[
                      parseFloat(project.latitude),
                      parseFloat(project.longitude),
                    ]}
                    zoom={15}
                    style={{ height: "100%", width: "100%" }}
                    scrollWheelZoom={false}
                  >
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    <Marker
                      position={[
                        parseFloat(project.latitude),
                        parseFloat(project.longitude),
                      ]}
                    />
                  </MapContainer>
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: "block" }}
                >
                  Lat: {parseFloat(project.latitude).toFixed(6)}, Lng:{" "}
                  {parseFloat(project.longitude).toFixed(6)}
                </Typography>
              </Paper>
            </Box>
          )}
        </Box>
      )}

      {/* Inventory Items Section */}
      {!loading && (
        <Box sx={{ mt: 2.5 }}>
          <Paper
            sx={{
              p: 3,
              borderRadius: theme.paper.borderRadius,
              "& .MuiTableContainer-root": {
                boxShadow: "none",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                mt: 2,
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Assignment
                  fontSize="small"
                  sx={{ color: theme.palette.primary.main }}
                />
                <Typography
                  variant="subtitle1"
                  fontWeight="700"
                  color="primary"
                >
                  Assigned Materials / Inventory
                </Typography>
                {projectItems.length > 0 && (
                  <Chip
                    label={projectItems.length}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      backgroundColor: theme.palette.primary.main + "18",
                      color: theme.palette.primary.main,
                      ml: 0.5,
                    }}
                  />
                )}
              </Box>
              <Button
                variant="contained"
                size="small"
                startIcon={<Add />}
                onClick={() => setOpenItemDialog(true)}
                sx={{
                  borderRadius: theme.button.borderRadius,
                  textTransform: "none",
                  backgroundColor: theme.palette.primary.main,
                }}
              >
                Assign Item
              </Button>
            </Box>

            {projectItems.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 4,
                  border: "1px dashed",
                  borderColor: "divider",
                  borderRadius: 2,
                }}
              >
                <Assignment
                  sx={{ fontSize: 40, color: "text.disabled", mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  No materials assigned to this project yet
                </Typography>
              </Box>
            ) : (
              <TableComponent
                columns={[
                  {
                    field: "item",
                    headerName: "Item Name",
                    render: (r) => r.inventory_transaction?.item?.name || "N/A",
                  },
                  {
                    field: "sku",
                    headerName: "SKU",
                    render: (r) => r.inventory_transaction?.item?.sku || "N/A",
                  },
                  {
                    field: "warehouse",
                    headerName: "Source Warehouse",
                    render: (r) =>
                      r.inventory_transaction?.warehouse?.name || "N/A",
                  },
                  {
                    field: "quantity",
                    headerName: "Quantity Assigned",
                    render: (r) => (
                      <span style={{ fontWeight: "bold" }}>{r.quantity}</span>
                    ),
                  },
                  {
                    field: "returned",
                    headerName: "Returned",
                    render: (r) =>
                      Number(r.returned_quantity) > 0 ? (
                        <Chip
                          label={r.returned_quantity}
                          size="small"
                          color="warning"
                        />
                      ) : ("-"),
                  },
                  {
                    field: "remaining",
                    headerName: "On Site",
                    render: (r) => (
                      <span
                        style={{
                          fontWeight: "bold",
                          color:
                            remainingQty(r) === 0
                              ? "rgba(0, 0, 0, 0.38)"
                              : "#2e7d32",
                        }}
                      >
                        {remainingQty(r)}
                      </span>
                    ),
                  },
                  {
                    field: "notes",
                    headerName: "Notes",
                    render: (r) => r.notes || "-",
                  },
                  {
                    field: "date",
                    headerName: "Date",
                    render: (r) => formatDate(r.created_at),
                  },
                ]}
                data={projectItems}
                pagination={false}
                actions={[
                  {
                    icon: <AssignmentReturn fontSize="small" />,
                    label: "Return to Stock",
                    tooltip: "Return unused material to stock",
                    backgroundColor: "#f0a500",
                    onClick: (row) => handleOpenReturn(row),
                  },
                ]}
              />
            )}
          </Paper>
        </Box>
      )}

      {/* Service History */}
      {!loading && project && (
        <ServiceHistorySection projectId={id} />
      )}

      {/* Create / Edit Service Dialog */}
      <CreateServiceDialog
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          setEditingService(null);
        }}
        saving={savingService}
        onSave={handleSaveService}
        project={project}
        service={editingService}
      />

      {/* Service Entry Dialog */}
      <ServiceEntryDialog
        open={entryDialogOpen}
        onClose={() => {
          setEntryDialogOpen(false);
          setSelectedEntry(null);
        }}
        entry={selectedEntry}
        employees={employees}
        onSave={handleEntrySave}
        saving={savingEntry}
      />

      {/* Assign Inventory Item Dialog */}
      <Dialog
        open={openItemDialog}
        onClose={() => setOpenItemDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Assign Material to Project</DialogTitle>
        <DialogContent
          dividers
          sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}
        >
          <FormControl fullWidth required>
            <InputLabel>Source Warehouse</InputLabel>
            <Select
              label="Source Warehouse"
              value={itemFormData.warehouse_id}
              onChange={(e) => {
                const wId = e.target.value;
                setItemFormData({
                  ...itemFormData,
                  warehouse_id: wId,
                  item_id: "",
                  quantity: "",
                });
                if (wId) loadWarehouseItemsForDialog(wId);
                else setWarehouseItems([]);
              }}
            >
              {warehouses.map((w) => (
                <MenuItem key={w.id} value={w.id}>
                  {w.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth required>
            <InputLabel>Performed By (Employee)</InputLabel>
            <Select
              label="Performed By (Employee)"
              value={itemFormData.performed_by}
              onChange={(e) =>
                setItemFormData({
                  ...itemFormData,
                  performed_by: e.target.value,
                })
              }
            >
              {employees.map((emp) => (
                <MenuItem key={emp.id} value={emp.id}>
                  {emp.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth required disabled={!itemFormData.warehouse_id || loadingWarehouseItems}>
            <InputLabel>{loadingWarehouseItems ? "Loading items..." : "Item"}</InputLabel>
            <Select
              label={loadingWarehouseItems ? "Loading items..." : "Item"}
              value={itemFormData.item_id}
              onChange={(e) =>
                setItemFormData({ ...itemFormData, item_id: e.target.value, quantity: "" })
              }
            >
              {warehouseItems.filter((wi) => wi.quantity > 0).length === 0 && !loadingWarehouseItems && (
                <MenuItem value="" disabled>
                  <em>{itemFormData.warehouse_id ? "No items with stock in this warehouse" : "Select a warehouse first"}</em>
                </MenuItem>
              )}
              {warehouseItems
                .filter((wi) => wi.quantity > 0)
                .map((wi) => (
                  <MenuItem key={wi.item_id} value={wi.item_id}>
                    {wi.item?.sku} - {wi.item?.name} (Stock: {wi.quantity})
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <TextField
            required
            fullWidth
            type="number"
            label="Quantity"
            inputProps={{
              min: 1,
              ...(itemFormData.item_id
                ? { max: warehouseItems.find((wi) => wi.item_id === itemFormData.item_id)?.quantity || 0 }
                : {}),
            }}
            value={itemFormData.quantity}
            onChange={(e) =>
              setItemFormData({
                ...itemFormData,
                quantity: parseInt(e.target.value) || "",
              })
            }
            helperText={
              itemFormData.item_id
                ? `Available: ${warehouseItems.find((wi) => wi.item_id === itemFormData.item_id)?.quantity || 0}`
                : ""
            }
          />
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Notes"
            value={itemFormData.notes}
            onChange={(e) =>
              setItemFormData({ ...itemFormData, notes: e.target.value })
            }
            placeholder="Reason for assignment..."
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenItemDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAssignItem}
            disabled={savingItem}
          >
            {savingItem ? "Assigning..." : "Assign Item"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Return Inventory Item Dialog */}
      <Dialog
        open={openReturnDialog}
        onClose={() => setOpenReturnDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Return Material to Stock
          {returnTarget && (
            <Typography variant="body2" color="text.secondary">
              {returnTarget.inventory_transaction?.item?.sku} —{" "}
              {returnTarget.inventory_transaction?.item?.name} (Assigned:{" "}
              {returnTarget.quantity}, On site: {remainingQty(returnTarget)})
            </Typography>
          )}
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}
        >
          <TextField
            required
            fullWidth
            type="number"
            label="Quantity to Return"
            inputProps={{ min: 1, max: remainingQty(returnTarget) }}
            value={returnFormData.quantity}
            onChange={(e) =>
              setReturnFormData({
                ...returnFormData,
                quantity: parseInt(e.target.value) || "",
              })
            }
            helperText={`Maximum returnable: ${remainingQty(returnTarget)}`}
          />
          <FormControl fullWidth required>
            <InputLabel>Performed By (Employee)</InputLabel>
            <Select
              label="Performed By (Employee)"
              value={returnFormData.performed_by}
              onChange={(e) =>
                setReturnFormData({
                  ...returnFormData,
                  performed_by: e.target.value,
                })
              }
            >
              {employees.map((emp) => (
                <MenuItem key={emp.id} value={emp.id}>
                  {emp.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Return To Warehouse</InputLabel>
            <Select
              label="Return To Warehouse"
              value={returnFormData.warehouse_id}
              onChange={(e) =>
                setReturnFormData({
                  ...returnFormData,
                  warehouse_id: e.target.value,
                })
              }
            >
              {warehouses.map((w) => (
                <MenuItem key={w.id} value={w.id}>
                  {w.name}
                  {w.id === returnTarget?.inventory_transaction?.warehouse_id
                    ? " (source)"
                    : ""}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Notes"
            value={returnFormData.notes}
            onChange={(e) =>
              setReturnFormData({ ...returnFormData, notes: e.target.value })
            }
            placeholder="Reason for return..."
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenReturnDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleReturnItem}
            disabled={savingReturn}
          >
            {savingReturn ? "Returning..." : "Return to Stock"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Project Confirm Dialog */}
      <ConfirmDialog
        open={confirmProjectDelete}
        onClose={() => setConfirmProjectDelete(false)}
        onConfirm={handleDeleteProject}
        title="Delete Project"
        message={`Are you sure you want to delete project #${project?.project_id}? All associated services will also be removed. This action cannot be undone.`}
        loading={deletingProject}
      />

      {/* Delete Service Confirm Dialog */}
      <ConfirmDialog
        open={confirmServiceDelete}
        onClose={() => {
          setConfirmServiceDelete(false);
          setServiceDeleteTarget(null);
        }}
        onConfirm={handleConfirmDeleteService}
        title="Delete Service Plan"
        message={`Are you sure you want to delete this ${serviceDeleteTarget?.service_type?.replace("_", " ")} service plan? This action cannot be undone.`}
        loading={deletingService}
      />
    </Box>
  );
};

export default ProjectDetailPage;
